import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  listAccessLinks,
  createAccessLink,
  deleteAccessLink,
} from '../../api/accessLinks';
import AdminLayout from './AdminLayout';
import AccessLinkQrModal from './AccessLinkQrModal';
import styles from './AdminAccessLinks.module.css';

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const isExhausted = (link) => link.max_uses != null && link.uses_count >= link.max_uses;

const getLinkStatus = (link) => {
  if (new Date(link.expires_at) <= new Date()) return { label: 'Истекла', type: 'expired' };
  if (isExhausted(link)) return { label: 'Исчерпана', type: 'redeemed' };
  if (link.uses_count > 0) return { label: 'Использована частично', type: 'partial' };
  return { label: 'Активна', type: 'active' };
};

// Возвращает строку "N / M" для лимитированных и "N (безлимит)" для безлимитных
const formatUsage = (link) => {
  if (link.max_uses == null) return `${link.uses_count} (∞)`;
  return `${link.uses_count} / ${link.max_uses}`;
};

const getRedeemUrl = (token) => `${window.location.origin}/redeem/${token}`;

const AdminAccessLinks = () => {
  const { token } = useAuth();

  const [links, setLinks] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    mode: 'duration',
    duration_days: '30',
    fixed_pay_until: '',
    expires_at: '',
    note: '',
    usage_type: 'single',  // 'single' | 'limited' | 'unlimited'
    max_uses: '5',
  });
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [createdLink, setCreatedLink] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [qrLink, setQrLink] = useState(null);

  const fetchLinks = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await listAccessLinks(token, { page, limit: 20, status: statusFilter });
      setLinks(data.links);
      setPagination(data.pagination);
    } catch (err) {
      setError(err.response?.data?.message || 'Ошибка загрузки ссылок');
    } finally {
      setIsLoading(false);
    }
  }, [token, page, statusFilter]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const resetForm = () => {
    setForm({
      mode: 'duration',
      duration_days: '30',
      fixed_pay_until: '',
      expires_at: '',
      note: '',
      usage_type: 'single',
      max_uses: '5',
    });
    setFormError('');
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formError) setFormError('');
  };

  const validateForm = () => {
    if (!form.expires_at) return 'Укажите срок жизни ссылки';
    const expiresAt = new Date(form.expires_at);
    if (isNaN(expiresAt.getTime())) return 'Некорректная дата срока жизни';
    if (expiresAt <= new Date()) return 'Срок жизни ссылки должен быть в будущем';

    if (form.mode === 'duration') {
      const days = parseInt(form.duration_days, 10);
      if (!Number.isFinite(days) || days <= 0) return 'Укажите положительное число дней';
    } else {
      if (!form.fixed_pay_until) return 'Укажите дату окончания подписки';
      const payUntil = new Date(form.fixed_pay_until);
      if (isNaN(payUntil.getTime())) return 'Некорректная дата окончания';
      if (payUntil <= new Date()) return 'Дата окончания должна быть в будущем';
    }

    if (form.usage_type === 'limited') {
      const uses = parseInt(form.max_uses, 10);
      if (!Number.isFinite(uses) || uses <= 0) return 'Укажите положительное число активаций';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        mode: form.mode,
        expires_at: new Date(form.expires_at).toISOString(),
        note: form.note.trim() || undefined,
      };
      if (form.mode === 'duration') {
        payload.duration_days = parseInt(form.duration_days, 10);
      } else {
        payload.fixed_pay_until = new Date(form.fixed_pay_until).toISOString();
      }

      if (form.usage_type === 'single') {
        payload.max_uses = 1;
      } else if (form.usage_type === 'limited') {
        payload.max_uses = parseInt(form.max_uses, 10);
      } else {
        payload.max_uses = null; // безлимит
      }

      const data = await createAccessLink(token, payload);
      setCreatedLink(data.link);
      setShowForm(false);
      resetForm();
      fetchLinks();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Не удалось создать ссылку');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (link) => {
    if (!window.confirm('Удалить ссылку? Это действие нельзя отменить.')) return;
    try {
      await deleteAccessLink(token, link.id);
      fetchLinks();
    } catch (err) {
      alert(err.response?.data?.message || 'Не удалось удалить ссылку');
    }
  };

  const handleCopy = async (link) => {
    try {
      await navigator.clipboard.writeText(getRedeemUrl(link.token));
      setCopiedId(link.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      alert('Не удалось скопировать ссылку');
    }
  };

  return (
    <AdminLayout>
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>Ссылки доступа</h1>
          <button
            className={styles.primaryBtn}
            onClick={() => {
              setShowForm((v) => !v);
              setCreatedLink(null);
              if (showForm) resetForm();
            }}
          >
            {showForm ? 'Отмена' : '+ Создать ссылку'}
          </button>
        </div>

        <p className={styles.description}>
          Одноразовые ссылки для предоставления платного доступа без оплаты. Активируется один раз; неиспользованные истекают по дате срока жизни.
        </p>

        {/* Уведомление о созданной ссылке */}
        {createdLink && (
          <div className={styles.createdBanner}>
            <div className={styles.createdLabel}>Ссылка создана:</div>
            <div className={styles.createdUrl}>
              <code>{getRedeemUrl(createdLink.token)}</code>
              <button
                className={styles.copyBtn}
                onClick={() => handleCopy(createdLink)}
              >
                {copiedId === createdLink.id ? '✓ Скопировано' : 'Копировать'}
              </button>
              <button
                className={styles.copyBtn}
                onClick={() => setQrLink(createdLink)}
              >
                QR-код
              </button>
            </div>
          </div>
        )}

        {/* Форма создания */}
        {showForm && (
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGrid}>
              <div className={styles.field}>
                <label className={styles.label}>Тип параметра подписки</label>
                <div className={styles.radioGroup}>
                  <label className={styles.radio}>
                    <input
                      type="radio"
                      name="mode"
                      value="duration"
                      checked={form.mode === 'duration'}
                      onChange={handleFieldChange}
                    />
                    <span>Длительность (дни)</span>
                  </label>
                  <label className={styles.radio}>
                    <input
                      type="radio"
                      name="mode"
                      value="fixed_date"
                      checked={form.mode === 'fixed_date'}
                      onChange={handleFieldChange}
                    />
                    <span>Конкретная дата окончания</span>
                  </label>
                </div>
              </div>

              {form.mode === 'duration' ? (
                <div className={styles.field}>
                  <label className={styles.label}>Длительность подписки (дней)</label>
                  <input
                    type="number"
                    name="duration_days"
                    value={form.duration_days}
                    onChange={handleFieldChange}
                    className={styles.input}
                    min="1"
                    placeholder="30"
                  />
                  <span className={styles.hint}>pay_until = (момент активации) + N дней</span>
                </div>
              ) : (
                <div className={styles.field}>
                  <label className={styles.label}>Дата окончания подписки</label>
                  <input
                    type="date"
                    name="fixed_pay_until"
                    value={form.fixed_pay_until}
                    onChange={handleFieldChange}
                    className={styles.input}
                  />
                  <span className={styles.hint}>pay_until будет равен этой дате при любой активации</span>
                </div>
              )}

              <div className={styles.field}>
                <label className={styles.label}>Срок жизни ссылки</label>
                <input
                  type="date"
                  name="expires_at"
                  value={form.expires_at}
                  onChange={handleFieldChange}
                  className={styles.input}
                />
                <span className={styles.hint}>После этой даты активация невозможна</span>
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Лимит активаций</label>
                <div className={styles.radioGroup}>
                  <label className={styles.radio}>
                    <input
                      type="radio"
                      name="usage_type"
                      value="single"
                      checked={form.usage_type === 'single'}
                      onChange={handleFieldChange}
                    />
                    <span>Одноразовая</span>
                  </label>
                  <label className={styles.radio}>
                    <input
                      type="radio"
                      name="usage_type"
                      value="limited"
                      checked={form.usage_type === 'limited'}
                      onChange={handleFieldChange}
                    />
                    <span>Многоразовая (N активаций)</span>
                  </label>
                  <label className={styles.radio}>
                    <input
                      type="radio"
                      name="usage_type"
                      value="unlimited"
                      checked={form.usage_type === 'unlimited'}
                      onChange={handleFieldChange}
                    />
                    <span>Без ограничения (до истечения срока)</span>
                  </label>
                </div>
                {form.usage_type === 'limited' && (
                  <input
                    type="number"
                    name="max_uses"
                    value={form.max_uses}
                    onChange={handleFieldChange}
                    className={styles.input}
                    min="1"
                    placeholder="5"
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Заметка (опционально)</label>
                <input
                  type="text"
                  name="note"
                  value={form.note}
                  onChange={handleFieldChange}
                  className={styles.input}
                  placeholder="Например: для Иванова И.И."
                />
              </div>
            </div>

            {formError && <p className={styles.formError}>{formError}</p>}

            <div className={styles.formActions}>
              <button type="submit" className={styles.primaryBtn} disabled={isSubmitting}>
                {isSubmitting ? 'Создание...' : 'Создать ссылку'}
              </button>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                Отмена
              </button>
            </div>
          </form>
        )}

        {/* Фильтр статуса */}
        <div className={styles.filters}>
          <select
            className={styles.select}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Все ссылки</option>
            <option value="active">Только активные</option>
            <option value="exhausted">Лимит исчерпан</option>
            <option value="expired">Истёкшие</option>
          </select>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {/* Таблица */}
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Параметры подписки</th>
                <th className={styles.th}>Срок жизни</th>
                <th className={styles.th}>Использовано</th>
                <th className={styles.th}>Статус</th>
                <th className={styles.th}>Последняя активация</th>
                <th className={styles.th}>Заметка</th>
                <th className={styles.th}>Создан</th>
                <th className={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className={styles.loadingCell}>Загрузка...</td>
                </tr>
              ) : links.length === 0 ? (
                <tr>
                  <td colSpan={8} className={styles.emptyCell}>Ссылок ещё нет</td>
                </tr>
              ) : (
                links.map((link) => {
                  const statusInfo = getLinkStatus(link);
                  const lastRedemption = link.redemptions?.[0];
                  const exhausted = isExhausted(link);
                  const expired = new Date(link.expires_at) <= new Date();
                  const canCopy = !exhausted && !expired;
                  const canDelete = link.uses_count === 0;
                  return (
                    <tr key={link.id} className={styles.row}>
                      <td className={styles.td}>
                        {link.mode === 'duration' ? (
                          <span>{link.duration_days} дн.</span>
                        ) : (
                          <span>до {formatDate(link.fixed_pay_until)}</span>
                        )}
                      </td>
                      <td className={styles.td}>{formatDate(link.expires_at)}</td>
                      <td className={styles.td}>
                        <span className={styles.usageBadge}>{formatUsage(link)}</span>
                      </td>
                      <td className={styles.td}>
                        <span className={`${styles.statusBadge} ${styles[statusInfo.type]}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className={styles.td}>
                        {lastRedemption ? (
                          <div className={styles.redeemerInfo}>
                            <div>{lastRedemption.user?.email || '—'}</div>
                            <div className={styles.redeemDate}>{formatDateTime(lastRedemption.redeemed_at)}</div>
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className={styles.td}>
                        <span className={styles.note}>{link.note || '—'}</span>
                      </td>
                      <td className={styles.td}>{formatDate(link.created_at)}</td>
                      <td className={styles.td}>
                        <div className={styles.rowActions}>
                          {canCopy && (
                            <button
                              className={styles.iconBtn}
                              onClick={() => handleCopy(link)}
                              title="Скопировать ссылку"
                            >
                              {copiedId === link.id ? '✓' : '📋'}
                            </button>
                          )}
                          {canCopy && (
                            <button
                              className={styles.iconBtn}
                              onClick={() => setQrLink(link)}
                              title="QR-код"
                            >
                              ▦
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className={`${styles.iconBtn} ${styles.dangerBtn}`}
                              onClick={() => handleDelete(link)}
                              title="Удалить"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Пагинация */}
        {pagination.totalPages > 1 && (
          <div className={styles.pagination}>
            <span className={styles.paginationInfo}>
              Всего: {pagination.total} | Страница {pagination.page} из {pagination.totalPages}
            </span>
            <div className={styles.paginationBtns}>
              <button
                className={styles.pageBtn}
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                ← Назад
              </button>
              <button
                className={styles.pageBtn}
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Вперёд →
              </button>
            </div>
          </div>
        )}
      </div>

      {qrLink && (
        <AccessLinkQrModal
          url={getRedeemUrl(qrLink.token)}
          note={qrLink.note || null}
          onClose={() => setQrLink(null)}
        />
      )}
    </AdminLayout>
  );
};

export default AdminAccessLinks;
